import { retry } from '@exodus/simple-retry'

import ClarityServer from './clarity.js'

const getTextFromResponse = async (response) => {
  try {
    const responseBody = await response.text()
    return responseBody.slice(0, 100)
  } catch {
    return ''
  }
}

const fetchJson = async (url, fetchOptions) => {
  const response = await fetch(url, fetchOptions)

  if (!response.ok)
    throw new Error(
      `${url} returned ${response.status}: ${
        response.statusText || 'Unknown Status Text'
      }. Body: ${await getTextFromResponse(response)}`
    )
  return response.json()
}

async function fetchJsonRetry(url, fetchOptions) {
  const waitTimes = ['3s']
  const fetchWithRetry = retry(fetchJson, { delayTimesMs: waitTimes })
  return fetchWithRetry(url, fetchOptions)
}

export default class ClarityServerV2 extends ClarityServer {
  constructor({ baseAssetName, uri }) {
    super({ baseAssetName, uri })
    this.baseApiPath = this.uri + `/api/v2/${this.baseAssetName}`
  }

  async getAllTransactions({ address, cursor }) {
    let { blockNumber } = this.#decodeCursor(cursor)
    blockNumber = blockNumber.toString()

    let transactions = []

    while (true) {
      const url = new URL(`${this.baseApiPath}/addresses/${address}/transactions`)
      url.searchParams.set('cursor', blockNumber)
      url.searchParams.set('withInput', 'true')

      const { transactions: txs, cursor: nextBlockNumber } = await fetchJsonRetry(url)

      if (txs.length === 0) {
        // fetch until no more new transactions
        blockNumber = nextBlockNumber
        break
      }

      if (nextBlockNumber <= blockNumber) {
        console.warn(
          `Invalid cursor: next block number is not greater than current block number at cursor ${blockNumber}`
        )

        // self healing, reset cursor to the smaller block number and remove the transactions greater than the block number
        transactions.push(...txs)
        transactions = transactions.filter((tx) => tx.blockNumber <= nextBlockNumber)
        blockNumber = nextBlockNumber
        break
      }

      transactions.push(...txs)
      blockNumber = nextBlockNumber
    }

    const newCursor = Buffer.alloc(8)
    newCursor.writeBigUInt64LE(BigInt(blockNumber), 0)

    return {
      transactions: {
        confirmed: transactions,
        pending: [],
      },
      cursor: newCursor,
    }
  }

  #decodeCursor(cursor) {
    if (Buffer.isBuffer(cursor)) {
      // New format: buffer containing only the block number
      if (cursor.length === 8) return { blockNumber: cursor.readBigUInt64LE(0) }

      // Old format: buffer with length 26
      if (cursor.length === 26) return { blockNumber: cursor.readBigInt64LE(10) }
    }

    // Doesn't match any known format, return default
    return { blockNumber: BigInt(0) }
  }

  async sendHttpRequest({ path, method, body }) {
    const url = new URL(`${this.baseApiPath}${path}`)
    const fetchOptions = {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
    }

    if (body) {
      fetchOptions.body = JSON.stringify(body)
    }

    const response = await fetchJson(url, fetchOptions)

    return this.handleJsonRPCResponse(response)
  }

  async sendRawTransaction(...params) {
    const request = this.sendRawTransactionRequest(...params)
    return this.sendHttpRequest({
      path: '/rpc',
      method: 'POST',
      body: request,
    })
  }
}
